import React, { useContext, useEffect, useState } from 'react';
import { createSearchParams, useNavigate, useParams } from 'react-router-dom';
import {
	CheckoutButton,
	ChevronRight,
	Column,
	Contact,
	ContentLoader,
	CustomerData,
	ErrorCrossIcon,
	HintMessage,
	IconButton,
	PaymentSelection,
	PriceInformation,
	Row,
	Toast,
	Typography,
	Wrapper,
	getHintContent,
	renderFlexServiceOrderSummary,
	withLocalization,
} from '@adac-mp/components';
import {
	ButtonState,
	ICustomer,
	Locales,
	OfferContext,
	OrderService,
	PaymentMethods,
	TollService,
	VignetteContext,
	getErrorTextFromResponse,
	getResponseErrorNumber,
	onOrderCompleted,
	setRouteLangParam,
	getLabelValue,
	ISepaInfo,
	IError,
	getEditPaymentCollection,
	ITypeVignettesOrderManagementFields,
	getUrlBasePath,
} from '@adac-mp/shared';
import Skeleton from 'react-loading-skeleton';
import { useViewport } from '@adac-mp/components/src/hooks/useViewport';
import { useQuery } from '@apollo/client';

export const EditPaymentMethodPage = () => {
	const navigate = useNavigate();
	const { lang } = useParams();
	const { viewport } = useViewport();
	const { orderData, contentfulLabels, updateContentfulLabels, updateNewOrderData } = useContext(VignetteContext);
	const { labels, updateAllCountries, allCountries } = useContext(OfferContext);
	const unsuccessfulPayment = orderData?.orderInfo.orderDetails.some(
		({ paymentNotSuccessful }) => paymentNotSuccessful,
	);
	const paymentMethods = orderData?.orderInfo.paymentMethods;
	const usedPaymentMethod = orderData?.orderInfo.selectedPaymentMethod.id;
	const hasFlexProduct =
		orderData?.orderInfo && 'flexProduct' in orderData.orderInfo && orderData.orderInfo.flexProduct;

	const [paymentMethod, setPaymentMethod] = useState<PaymentMethods | undefined>(
		usedPaymentMethod ? (String(usedPaymentMethod) as PaymentMethods) : undefined,
	);
	const [sepaInfo, setSepaInfo] = useState<ISepaInfo | undefined>(undefined);
	const [customer, setCustomer] = useState<ICustomer | undefined>();
	const [validateFields, setValidateFields] = useState(false);
	const [customerFilled, setCustomerFilled] = useState<boolean>(false);
	const [sepaInfoFilled, setSepaInfoFilled] = useState<boolean>(false);

	const [buttonState, setButtonState] = useState<ButtonState>('active');
	const [apiError, setApiError] = useState<string | undefined>(undefined);
	const [dataLoading, setLoading] = useState(false);
	const paymentLabel =
		orderData?.orderInfo.paymentMethods.find(({ id }) => String(id) === paymentMethod)?.name ??
		String(paymentMethod);
	const openAmountDecimal = orderData?.orderInfo.orderDetails.reduce(
		(n, { openAmount }) => n + openAmount.decimalAmount,
		0,
	);
	const { error, data, loading } = useQuery(getEditPaymentCollection, {
		variables: { lang: lang || 'de' },
	});

	useEffect(() => {
		if (!error) {
			updateContentfulLabels(data?.vignettesOrderManagementCollection.items[0]);
		}
	}, [data]);

	const currentLabels = contentfulLabels as ITypeVignettesOrderManagementFields;

	const handleOrderException = (errors: IError[]) => {
		const errorNumber = getResponseErrorNumber(errors);
		if (errorNumber === 3300) {
			navigate(getUrlBasePath(`/link-expired${setRouteLangParam(lang)}`), { replace: true });
		}
		const errorText = getErrorTextFromResponse(errors, getLabelValue('mp.generalError', labels));
		setApiError(errorText);
	};

	useEffect(() => {
		if (!orderData || lang) {
			setLoading(true);
			const orderHash = sessionStorage.getItem('orderHash') ?? '';
			OrderService.getOrderInfo(
				{
					orderHash,
				},
				lang as Locales,
			).then((res) => {
				setLoading(false);
				if (res.code === 200) {
					if (!res.orderInfo.flexProduct) {
						const params = createSearchParams();
						params.set('orderHash', String(orderHash));
						navigate(getUrlBasePath(`/order-management`, params), { replace: true });
						return;
					}
					updateNewOrderData(res);
					setPaymentMethod(String(res.orderInfo.selectedPaymentMethod.id) as PaymentMethods);
					sessionStorage.setItem('orderHash', res.orderInfo.orderHash);
					sessionStorage.setItem('token', res.token);
				} else {
					handleOrderException(res.errors);
				}
			});
			TollService.getCountries(lang).then((countryData) => {
				updateAllCountries(countryData.countries);
			});
		}
	}, [lang]);

	useEffect(() => {
		setButtonState(
			customerFilled && (!(paymentMethod === '3') || (paymentMethod === '3' && sepaInfoFilled))
				? 'active'
				: 'disabled',
		);
	}, [customerFilled, sepaInfoFilled, paymentMethod]);

	const renderPaymentMethods = () =>
		paymentMethods ? (
			<PaymentSelection
				showHint
				paymentMethods={paymentMethods}
				value={paymentMethod}
				validateFields={validateFields}
				setValue={setPaymentMethod}
				updateSepaInfo={setSepaInfo}
				setSepaInfoFilled={setSepaInfoFilled}
			/>
		) : (
			<Skeleton width="100%" height={60} className="rounded" />
		);

	const renderPageHeading = () => (
		<Typography
			component="h1"
			size="h1"
			color="title"
			variant="header"
			className={`pl-4 mb-5 ${unsuccessfulPayment ? 'md:mb-3' : 'md:mb-8'}`}>
			{(hasFlexProduct ? currentLabels?.updatePaymentMethod : currentLabels?.updateBankDetails) ||
				'Zahlungsdaten aktualisieren'}
		</Typography>
	);

	const renderFlexProductsOverview = () =>
		orderData?.orderInfo.orderDetails
			.filter(({ flexProduct }) => flexProduct)
			.map((order, i) => (
				<React.Fragment key={i}>
					{renderFlexServiceOrderSummary(order, currentLabels as any, labels, true)}
				</React.Fragment>
			));

	const renderPriceInformation = () => (
		<PriceInformation
			className="bg-gray-600 rounded py-3.5 px-4 mb-5"
			totalPrice={{
				label: currentLabels?.pendingAmount || 'Ausstehender Betrag',
				amount: `${openAmountDecimal?.toFixed(2).replace('.', ',')}€`,
				decimalAmount: 0,
			}}
		/>
	);

	const renderPaymentNotSuccessfulMessage = () => (
		<Typography className="mb-6 pl-4" component="p" color="title" size="lg">
			{currentLabels?.paymentNotSuccessfulText ||
				'Ihre letzte Zahlung war leider nicht erfolgreich. Bitte aktualisieren Sie Ihre Zahlungsmethode, um die Transaktion abschließen zu können. Alle künftigen Abbuchungen nehmen wir dann mit dieser Zahlungsmethode vor.'}
		</Typography>
	);

	const handleClick = () => {
		setValidateFields(true);
		const scrollPos = viewport === 'desktop' ? 355 : 840;
		window.scrollTo({ top: scrollPos, behavior: 'smooth' });
	};

	const renderUpdatePaymentMethodButton = () =>
		paymentMethod && (
			<CheckoutButton
				paymentUpdate
				className="mb-5"
				variant="vignette"
				label={currentLabels?.updateNow || 'Jetzt aktualisieren'}
				customer={customer}
				buttonState={buttonState}
				sepaData={sepaInfo}
				paymentMethod={paymentMethod}
				paymentLabel={paymentLabel}
				paymentLabels={{ creditCard: currentLabels?.creditCardDetails, sepa: currentLabels?.sepaMandat }}
				setError={setApiError}
				updateButtonState={setButtonState}
				onOrderCompleted={onOrderCompleted(navigate)}
				onClick={handleClick}
			/>
		);

	const renderBackToOrderManagementButton = () => (
		<IconButton
			className="border-none inline-flex items-center gap-2 text-gray-200 font-bold text-xl transition-colors group hover:text-gray-100"
			onClick={() => {
				const orderHash = sessionStorage.getItem('orderHash');
				const params = createSearchParams();
				params.set('orderHash', String(orderHash));
				navigate(getUrlBasePath(`/order-management${setRouteLangParam(lang)}`, params));
			}}>
			<ChevronRight fill="#3D3D3D" className="rotate-180 group-hover:[&>*]:fill-gray-100" />
			{currentLabels?.backToOverview || 'Zurück zur Bestellübersicht'}
		</IconButton>
	);

	const renderHintMessage = () => (
		<HintMessage
			className="mt-4 mb-3"
			hints={getHintContent(
				'editPaymentPersonalData',
				[
					{
						tip: 'Hinweis:',
						message:
							'Die Erhebung und Verarbeitung persönlicher Daten erfolgt ausschließlich zu Autorisierungszwecken in der Zahlungsabwicklung.',
					},
				],
				currentLabels?.hintMessages,
			)}
		/>
	);

	const renderCustomerSection = () => (
		<Column className="md:order-1 md:basis-7/12 md:mr">
			{viewport === 'desktop' && renderPageHeading()}
			{unsuccessfulPayment && renderPaymentNotSuccessfulMessage()}
			{renderPaymentMethods()}
			<CustomerData
				labels={currentLabels?.personalData}
				variant="updatepayment"
				customer={customer}
				allCountries={hasFlexProduct ? allCountries : undefined}
				validateFields={validateFields}
				setCustomerFilled={setCustomerFilled}
				updateCustomer={setCustomer}
			/>
			{renderHintMessage()}
			{renderUpdatePaymentMethodButton()}
			{renderBackToOrderManagementButton()}
		</Column>
	);

	const renderProductsSection = () => (
		<Column className="md:order-3 md:basis-5/12">
			{renderFlexProductsOverview()}
			{unsuccessfulPayment && renderPriceInformation()}
		</Column>
	);

	return (
		<>
			{dataLoading && loading ? (
				<ContentLoader isLoading={dataLoading && loading} hasPortal={false} />
			) : (
				<Wrapper>
					{viewport === 'mobile' && renderPageHeading()}
					<Row>
						{renderProductsSection()}
						{renderCustomerSection()}
					</Row>
				</Wrapper>
			)}
			<Contact />
			{apiError && (
				<Toast content={apiError} icon={<ErrorCrossIcon />} onToastFinished={() => setApiError(undefined)} />
			)}
		</>
	);
};

export const EditPaymentMethod = withLocalization(EditPaymentMethodPage);
